import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import SuiteApi from "../../api/SuiteApi";
import UserApi from "../../api/UserApi";
import IconApp from "../../Icons/AppIcon";
import IconHidePass from "../../Icons/HidePassword";
import IconInfo from "../../Icons/NotificationMessage/Info";
import IconShowPass from "../../Icons/ShowPassword";
import { ApplicationInfo } from "../../Model/ApplicationInfo";
import { ENotificationType } from "../../Model/ENotificationType";
import MtiButton from "../Buttons/MtiButton";
import InlineNotification from "../Common/InlineNotification";
import TextBox from "../Common/TextBox";
import "./Login.css";
import UserUtil from "../../Utility/UserUtil";
import { removeNotification } from "../../redux/actions/NotificationAction";

const PASSWORD_MISSMATCH = "Passwords don't match. Try again.";
const PASSWORD_EMPTY = "Enter a password.";
const PASSWORD_CONFIRM_EMPTY = "Confirm your password.";
const PASSWORD_WEAK =
  "Password must be 6 to 20 characters which contain at least one numeric digit, one uppercase and one lowercase letter";

const getInitialInlineNotif = (location: string) => {
  if (location.indexOf("error") != -1) {
    return {
      notificationType: ENotificationType.Error,
      message:
        "Request failed, please contact administrator to get OTP number.",
    };
  }

  if (location.indexOf("success") != -1) {
    return {
      notificationType: ENotificationType.Success,
      message:
        "OTP has been sent to your email. It will expired in 1 hour, please open your email and enter the code to verify your account.",
    };
  }

  return {
    message: "",
    notificationType: null as ENotificationType | null,
  };
};

export default function ResetPassword() {
  const [userOtp, setUserOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [inLineNotification, setInLineNotification] = useState(
    getInitialInlineNotif(window.location.href)
  );
  const [showNotification, setShowNotification] = useState(true);
  const [passwordError, setPasswordError] = useState(null as string | null);
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  const [instrumentTypeName, setInstrumentTypeName] = useState("ROCK IMAGER");
  const [applicationInfo, setApplicationInfo] = useState({} as ApplicationInfo);
  const [disableButton, setDisableButton] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    SuiteApi.getInstrumentTypeName().then((value) => {
      setInstrumentTypeName(value);
    });
    SuiteApi.getApplicationInfo().then((value) => {
      setApplicationInfo(value);
    });
  }, []);

  const onEnterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      proceed();
    }
    if(disableButton){
      setDisableButton(false);
    }
  };
  const resetFailStatus = () => {};

  const cancel = () => {
    history.push("/");
  };

  const toggleShowPass = () => {
    setShowPassword(!showPassword);
  };

  const closeNotification = () => {
    setShowNotification(false);
    setDisableButton(false);
  };

  const proceed = () => {
    if (!UserUtil.IsUserPasswordStrong(password)) {
      setPasswordError(PASSWORD_WEAK);
      return;
    }
    if (passwordConfirm === "") {
      setPasswordError(PASSWORD_CONFIRM_EMPTY);
      return;
    }
    if (password === "") {
      setPasswordError(PASSWORD_EMPTY);
      return;
    }
    if (password !== passwordConfirm) {
      setPasswordError(PASSWORD_MISSMATCH);
      return;
    }

    setPasswordError(null);
    UserApi.resetPassword({
      userOtp: userOtp,
      newPassword: password,
    })
      .then(() => {
        history.push("/");
      })
      .catch((error) => {
        dispatch(removeNotification());
        setInLineNotification({
          notificationType: ENotificationType.Error,
          message: "Invalid OTP. Please enter the correct one.",
        });
        setDisableButton(true);
        setShowNotification(true);
      });
  };

  return (
    <div className="login-page">
      <div
        className="login-form"
        style={{
          width: "600px",
        }}
      >
        <div className="login-title">
          <div className="title-container">
            <IconApp width="34" height="34" viewBox="0 0 48 48" fill="none" />
            <p>
              {instrumentTypeName}
              <sup>®</sup>
            </p>
          </div>
        </div>
        <div>
          {inLineNotification.notificationType != null && showNotification && (
            <InlineNotification
              type={inLineNotification.notificationType}
              message={inLineNotification.message}
              onClose={closeNotification}
              style={{
                width: "540px",
              }}
            />
          )}
        </div>
        <TextBox
          name="Username"
          value={userOtp}
          placeHolder="OTP Number"
          onKeyDown={(event) => setUserOtp(event.currentTarget.value)}
          onChange={(value) => setUserOtp(value as string)}
          onClick={resetFailStatus}
          onKeyPress={onEnterKeyPress}
          supportAutoFill={false}
          onPaste={() => setDisableButton(false)}
        />
        <div className="login-password">
          <TextBox
            name="password"
            variant={showPassword ? "text" : "password"}
            placeHolder="New Password"
            value={password}
            onKeyDown={(event) => setPassword(event.currentTarget.value)}
            onChange={(value) => setPassword(value as string)}
            onClick={resetFailStatus}
            onKeyPress={onEnterKeyPress}
            supportAutoFill={false}
            onPaste={() => setDisableButton(false)}
            style={{
              border:
                !passwordError != null
                  ? "1px inset #828282"
                  : "1px solid #C92932",
            }}
          />
          <button onClick={toggleShowPass} className="login-password-button" tabIndex={-1}>
            {showPassword && <IconShowPass width="17" height="17" />}
            {!showPassword && <IconHidePass width="17" height="17" />}
          </button>
        </div>

        <div className="login-password">
          <TextBox
            name="confirm"
            variant={showPassword ? "text" : "password"}
            placeHolder="Confirm New Password"
            value={passwordConfirm}
            onKeyDown={(event) => setPasswordConfirm(event.currentTarget.value)}
            onChange={(value) => setPasswordConfirm(value as string)}
            onClick={resetFailStatus}
            onKeyPress={onEnterKeyPress}
            supportAutoFill={true}
            onPaste={() => setDisableButton(false)}
            style={{
              border:
                !passwordError != null
                  ? "1px inset #828282"
                  : "1px solid #C92932",
            }}
          />
          <button onClick={toggleShowPass} className="login-password-button" tabIndex={-1}>
            {showPassword && <IconShowPass width="17" height="17" />}
            {!showPassword && <IconHidePass width="17" height="17" />}
          </button>
        </div>
        {passwordError != null && (
          <div className="login-fail">
            <IconInfo fill="#C92932" width="10" height="10" />
            <p>{passwordError}</p>
          </div>
        )}

        <div className="login-buttons">
          <MtiButton
            size="large"
            onClick={proceed}
            disabled={
              userOtp.trim().length === 0 ||
              password.trim().length === 0 ||
              passwordConfirm.trim().length === 0 ||
              disableButton
            }
          >
            Proceed
          </MtiButton>
          <MtiButton
            size="large"
            onClick={cancel}
            style={{
              margin: "0px 10px",
            }}
            disabled={disableButton}
          >
            Cancel
          </MtiButton>
        </div>
      </div>
      <div
        className="login-footer"
        style={{
          width: "600px",
        }}
      >
        <div>
          <p>{"version " + applicationInfo.productVersion}</p>
        </div>
        <div>
          <p>{applicationInfo.companyName}</p>
        </div>
        <div>
          <p>{`©${applicationInfo.copyrightYear}`}</p>
        </div>
      </div>
    </div>
  );
}
